<template>
    <div class="w-full">
        <!-- Swiper container -->
        <div class="swiper-container w-full" :class="additionalContainerClasses" ref="container">
            <ul class="swiper-wrapper flex flex-nowrap" :class="wrapperClasses" :role="isTab ? 'tablist' : null">
                <li
                    v-for="(el, i) in childElements()"
                    :key="i"
                    class="swiper-slide flex-shrink-0 max-w-full"
                    :class="[slideClasses, index === i ? 'slide-active' : '']"
                    :inert="fullWidthSlides && index !== i"
                    @click="slideClicked">
                    <!-- Render the child element -->
                    <component :is="el" />
                </li>
            </ul>
        </div>

        <!-- Pagination slot -->
        <template v-if="$slots.pagination && !editMode">
            <slot
                name="pagination"
                :index="index + 1"
                :total="numSlides"
                :teaser="currentSlide"
                :go="goTo"
                :isPlaying="isPlaying" />
        </template>

        <!-- Controls slot -->
        <template v-if="$slots.controls">
            <slot
                name="controls"
                :prev="prev"
                :next="next"
                :index="index"
                :show="prevVisible || nextVisible"
                :prevVisible="prevVisible"
                :nextVisible="nextVisible"
                :isPlaying="isPlaying" />
        </template>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import Swiper, { EffectFade } from 'swiper';
import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindConfig from '../../../tailwind.config.js';

Swiper.use([EffectFade]);

export default {
    provide() {
        return {
            registerSlide: this.register,
        };
    },
    props: {
        direction: {
            type: String,
            default: 'horizontal',
            validator(value: string) {
                // The value must match one of these strings
                return ['horizontal', 'vertical'].includes(value);
            },
        },
        contrast: { type: Boolean, default: false },
        loop: { type: Boolean, default: false },
        gap: {
            // @Prop({ default: 0 }) gap: number | { [breakpoint: string]: number };
            type: [Number, Object as PropType<{ [breakpoint: string]: number }>],
            default: 0,
        },
        containerClasses: { type: String, default: '' },
        slideClasses: { type: String, default: '' },
        start: { type: Number, default: 0 },
        speed: { type: Number, default: 300 },
        slidesOffsetAfter: {
            // @Prop({ default: 0 }) slidesOffsetAfter!: number | { [breakpoint: string]: number };
            type: [Number, Object as PropType<{ [breakpoint: string]: number }>],
            default: 0,
        },
        showThumbs: { type: Boolean, default: false },
        fullHeightSlides: { type: Boolean, default: false },
        fullWidthSlides: { type: Boolean, default: false },
        static: { type: Boolean, default: false },
        editMode: { type: Boolean, default: false },
        slidesPerView: {
            // @Prop({ default: 'auto' }) slidesPerView: number | 'auto' | { [breakpoint: string]: number | 'auto' };
            type: [Number, String, Object as PropType<{ [breakpoint: string]: number | 'auto' }>],
            default: 'auto',
        },
        hideOverflow: { type: Boolean, default: true },
        isTab: { type: Boolean, default: false },
    },
    data() {
        return {
            slides: [],
            swiper: null,
            index: this.start,
            options: {
                watchOverflow: true,
                observer: true,
                loop: this.loop,
                speed: this.speed,
                direction: this.direction,
                touchReleaseOnEdges: true,
                allowSlidePrev: false,
                initialSlide: this.start,
                observeParents: true,
                watchSlidesProgress: true,
                watchSlidesVisibility: true,
                shortSwipes: false,
                longSwipesRatio: 0.1,
                longSwipesMs: 100,
                noSwipingSelector: 'video',
                effect: this.direction === 'vertical' ? 'fade' : 'slide',
                fadeEffect: {
                    crossFade: true,
                },
            },

            twConfig: resolveConfig(tailwindConfig),

            observing: false,
            prevVisible: false,
            nextVisible: false,
            isPlaying: false,
        };
    },
    computed: {
        numSlides(): number {
            return this.childElements().length;
        },
        currentSlide(): any {
            const index = this.index - this.start < 0 ? this.slides.length - 1 : this.index - this.start;
            if (this.slides.length && this.slides.length > index) {
                return this.slides[index];
            }
            return null;
        },
        additionalContainerClasses(): string {
            const classes = [this.containerClasses];
            if (this.hideOverflow) {
                classes.push('overflow-hidden');
            }
            return classes.filter(x => x.length).join(' ');
        },
        wrapperClasses(): string {
            if (typeof this.direction === 'string') {
                return this.direction === 'vertical' ? 'flex-col w-full h-full' : 'flex-row';
            }
            // md:flex-col lg:flex-col xl:flex-col md:flex-row lg:flex-row xl:flex-row
            const classes = [];
            Object.entries(this.direction).forEach(x => {
                const bp = this.breakpointSize(x[0]);
                const dir = x[1] === 'vertical' ? 'flex-col' : 'flex-row';
                if (bp > 0) {
                    classes.push(`${x[0]}:${dir}`);
                } else {
                    classes.push(dir);
                }
            });
            return classes.join(' ');
        },
    },
    mounted(): void {
        if (this.editMode) {
            return;
        }

        this.setBreakpointOption(this.direction, 'direction');
        this.setBreakpointOption(this.gap, 'spaceBetween');
        this.setBreakpointOption(this.slidesPerView, 'slidesPerView');
        this.setBreakpointOption(this.slidesOffsetAfter, 'slidesOffsetAfter');

        this.swiper = new Swiper(this.$refs['container'], this.options);
        this.swiper.on('activeIndexChange', this.indexChanged);

        this.observeSlideVisibility();
    },
    updated(): void {
        if (!this.observing) {
            this.observeSlideVisibility();
        }
    },
    methods: {
        indexChanged({ activeIndex, realIndex }): void {
            this.index = this.loop ? realIndex : activeIndex;
        },
        next(): void {
            if (this.swiper) {
                if (this.loop) {
                    this.index = this.index + 1 >= this.slides.length ? 0 : this.index + 1;
                } else if (this.index + 1 < this.slides.length) {
                    this.index++;
                }
                this.swiper.slideNext();
            }
        },
        prev(): void {
            if (this.swiper) {
                if (this.loop) {
                    this.index = this.index > 0 ? this.index - 1 : this.slides.length - 1;
                } else if (this.index > 0) {
                    this.index--;
                }
                this.swiper.slidePrev();
            }
        },
        goTo(index: number): void {
            if (this.swiper) {
                this.swiper.slideTo(index);
                this.index = index;
            }
        },
        /**
         * This method is called by slides.
         *
         * @param slide A slide that wants to register itself
         */
        register(slide: any): void {
            this.slides.push(slide);
        },
        slideClicked(e) {
            const index = this.slides.findIndex(x => x._id() === e.target.dataset.id);
            if (index >= 0 && index < this.slides.length) {
                this.goTo(index);
            }
        },
        breakpointSize(name: string): number {
            if (Object.prototype.hasOwnProperty.call(this.twConfig.theme.screens, name)) {
                return parseInt(this.twConfig.theme.screens[name]);
            }
            return -1;
        },
        setBreakpointOption(value: string | number | object, name: string): void {
            const breakpoints = this.options.breakpoints || {};
            if (typeof value === 'number' || typeof value === 'string') {
                this.options[name] = value;
                return;
            }
            Object.entries(value).forEach(x => {
                const bp = this.breakpointSize(x[0]);
                if (bp > 0) {
                    if (!Object.prototype.hasOwnProperty.call(breakpoints, bp)) {
                        breakpoints[bp] = {};
                    }
                    breakpoints[bp][name] = x[1];
                } else {
                    this.options[name] = x[1];
                }
            });
            if (!this.options.breakpoints) {
                this.options.breakpoints = breakpoints;
            }
        },
        observeSlideVisibility(): void {
            if (this.slides && this.slides.length > 0) {
                this.slides
                    .map(slide => slide.$el.parentElement)
                    .forEach(el => {
                        const options: IntersectionObserverInit = {
                            threshold: 0.95,
                            root: this.$refs['container'],
                        };
                        // eslint-disable-next-line no-undef
                        const observer = new IntersectionObserver(entries => {
                            const isLeft = entries[0].target.isSameNode(this.slides[0].$el.parentElement);
                            const isRight = entries[0].target.isSameNode(
                                this.slides[this.slides.length - 1].$el.parentElement
                            );
                            if (isLeft) this.prevVisible = !entries[0].isIntersecting;
                            if (isRight) this.nextVisible = !entries[0].isIntersecting;
                        }, options);
                        observer.observe(el);
                    });
                this.observing = true;
            }
        },
        childElements(): any[] {
            return this.normalizeSlotContent(this.$slots.default()).filter(c => c.type);
        },
        normalizeSlotContent(slotContent) {
            if (!Array.isArray(slotContent)) return slotContent.children;
            if (slotContent.length === 1 && slotContent[0].type === Symbol.for('v-fgt')) {
                return slotContent[0].children;
            }
            return slotContent;
        },
    },
};
</script>
